import React from "react";
import { Check, Sparkles, Play } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

export interface ToolBlockProps {
  verb: string;
  title: string;
  description: string;
  features: string[];
  lawAtWork: React.ReactNode;
  quote: string;
  quoteAuthor: string;
  reverse?: boolean;
  media?: React.ReactNode;
  mediaTag?: string;
}

const DefaultMedia: React.FC<{ tag?: string }> = ({ tag }) => (
  <div className="relative aspect-[16/10] cosmic-card overflow-hidden">
    <div className="absolute inset-0 bg-cosmic-radial opacity-50" />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50%] h-[50%] rounded-full bg-cosmic-500/20 blur-3xl animate-pulse" />
    <div className="absolute inset-0 grid place-items-center">
      <div className="w-16 h-16 rounded-full bg-cosmic-500/20 border border-cosmic-400/40 backdrop-blur-sm grid place-items-center">
        <Play className="h-6 w-6 text-cosmic-200 ml-0.5 fill-current" />
      </div>
    </div>
    {tag && (
      <span className="absolute left-4 bottom-4 text-xs text-cosmic-200 bg-cosmic-900/60 border border-cosmic-400/30 backdrop-blur-sm px-3 py-1.5 rounded-full">
        {tag}
      </span>
    )}
  </div>
);

const ToolBlock: React.FC<ToolBlockProps> = ({
  verb,
  title,
  description,
  features,
  lawAtWork,
  quote,
  quoteAuthor,
  reverse,
  media,
  mediaTag,
}) => {
  return (
    <div className="py-12 md:py-16">
      <div
        className={`grid md:grid-cols-2 gap-10 md:gap-14 items-center ${
          reverse ? "md:[&>*:first-child]:order-2" : ""
        }`}
      >
        <ScrollReveal direction={reverse ? "right" : "left"}>
          <div>
            <span className="inline-block px-3 py-1 rounded-full text-[11px] font-medium tracking-[0.2em] uppercase text-cosmic-200 border border-cosmic-400/30 bg-cosmic-900/50 backdrop-blur-sm">
              {verb}
            </span>
            <h3 className="mt-4 text-3xl md:text-4xl font-display font-bold text-white">{title}</h3>
            <p className="mt-3 text-lg text-white/70 leading-relaxed">{description}</p>

            <ul className="mt-6 space-y-3">
              {features.map((f) => (
                <li key={f} className="flex gap-3 items-start">
                  <div className="mt-1 w-5 h-5 rounded-full bg-cosmic-500/20 border border-cosmic-400/40 grid place-items-center flex-none">
                    <Check className="h-3 w-3 text-cosmic-200" />
                  </div>
                  <span className="text-white/85">{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-7 cosmic-card p-5">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-cosmic-200 mb-2">
                <Sparkles className="h-3 w-3" />
                The law at work
              </div>
              <p className="text-white/75 leading-relaxed">{lawAtWork}</p>
            </div>

            <div className="mt-6 pl-4 border-l-2 border-cosmic-400/30">
              <p className="italic text-white/70 leading-snug">"{quote}"</p>
              <div className="mt-2 text-sm text-white/40">{quoteAuthor}</div>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal direction={reverse ? "left" : "right"} delay={120}>
          {media ?? <DefaultMedia tag={mediaTag} />}
        </ScrollReveal>
      </div>
    </div>
  );
};

export default ToolBlock;
